// Alliance recruiting (spec §3.6, from Warlord): find and invite commanders, answer applications.
import type { AllianceInvite, MemberAlliance } from '@stars-and-ships/api-types';
import { useMutation } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { endpoints } from '../../api/endpoints';
import { useAllianceInvites, usePlayerSearch } from '../../api/queries';
import { Button, ErrorNotice, inputFrame, Notice, Panel, Spinner } from '../ui';
import { useRefresh } from './shared';

export function RecruitingTab({
  galaxyId,
  alliance,
}: {
  galaxyId: string;
  alliance: MemberAlliance;
}) {
  const invites = useAllianceInvites(galaxyId, alliance.id, true);
  const refresh = useRefresh(galaxyId);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const players = usePlayerSearch(galaxyId, query);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState<string | null>(null);
  const handlers = {
    onError: setError,
  };
  const invite = useMutation({
    mutationFn: (p: { playerId: string; displayName: string }) =>
      endpoints.invitePlayer(galaxyId, alliance.id, p.playerId),
    onSuccess: (_r: unknown, p: { playerId: string; displayName: string }) => {
      setError(null);
      setSent(`Invitation sent to ${p.displayName}.`);
      refresh();
    },
    ...handlers,
  });
  const answer = useMutation({
    mutationFn: ({ item, accept }: { item: AllianceInvite; accept: boolean }) =>
      accept
        ? endpoints.acceptInvite(galaxyId, item.id).then(() => undefined)
        : endpoints.declineInvite(galaxyId, item.id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    ...handlers,
  });
  const memberIds = new Set(alliance.members.map((m) => m.playerId));
  return (
    <div className="space-y-4">
      <Panel title="Invite commanders">
        <form
          className="mb-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSent(null);
            setQuery(search.trim());
          }}
        >
          <input
            aria-label="Search commanders"
            placeholder="Commander name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputFrame} min-w-0 flex-1`}
          />
          <Button type="submit" variant="secondary" aria-label="Find commander">
            <Search className="size-4" aria-hidden />
          </Button>
        </form>
        {error ? <ErrorNotice error={error} /> : null}
        {sent ? <Notice tone="success">{sent}</Notice> : null}
        {players.data ? (
          <ul className="mb-3 space-y-2">
            {players.data.items.length === 0 ? (
              <li className="text-sm text-muted">No commander with that name.</li>
            ) : null}
            {players.data.items.map((p) => (
              <li
                key={p.playerId}
                data-player-result={p.displayName}
                className="flex items-center justify-between gap-2 hud-cut-sm border border-line bg-void/60 p-3 text-sm"
              >
                <span className="text-text">
                  {p.displayName}
                  {p.allianceTag ? <span className="text-info"> [{p.allianceTag}]</span> : null}
                </span>
                {memberIds.has(p.playerId) ? (
                  <span className="text-xs text-faint">member</span>
                ) : p.allianceTag ? (
                  <span className="text-xs text-faint">in an alliance</span>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={invite.isPending}
                    onClick={() => invite.mutate(p)}
                  >
                    Invite
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </Panel>
      <Panel title="Open invitations and applications">
        {invites.isPending ? <Spinner /> : null}
        {invites.data && invites.data.items.length === 0 ? (
          <p className="text-sm text-muted">Nothing open.</p>
        ) : null}
        <ul className="space-y-2">
          {invites.data?.items.map((item) => (
            <li
              key={item.id}
              data-alliance-invite={item.player.displayName}
              className="flex flex-wrap items-center justify-between gap-2 hud-cut-sm border border-line bg-void/60 p-3 text-sm"
            >
              <div className="min-w-0">
                <p className="text-text">{item.player.displayName}</p>
                <p className="text-xs text-muted">
                  {item.kind === 'invite'
                    ? `Invited by ${item.invitedBy?.displayName ?? 'an officer'}`
                    : 'Applied to join'}
                </p>
                {item.message ? <p className="mt-1 text-xs text-muted">“{item.message}”</p> : null}
              </div>
              <div className="flex gap-2">
                {item.kind === 'application' ? (
                  <Button
                    disabled={answer.isPending}
                    onClick={() => answer.mutate({ item, accept: true })}
                  >
                    Accept
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  disabled={answer.isPending}
                  onClick={() => answer.mutate({ item, accept: false })}
                >
                  {item.kind === 'application' ? 'Decline' : 'Withdraw'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
